<template>
  <v-container fluid>
    <v-row align="center" class="mb-2">
      <v-col>
        <HeaderLabel :title="pageTitle" :show-back-button="true" />
      </v-col>
      <v-col cols="auto">
        <ValidationObserver ref="form" v-slot="{ invalid }">
          <v-btn color="primary" :loading="isSubmitting" :disabled="invalid" @click="save"
            >Save</v-btn
          >
        </ValidationObserver>
      </v-col>
    </v-row>
    <ValidationObserver ref="form" v-slot="{ invalid }">
      <v-card class="custom-container-box">
        <v-card-text>
          <v-row>
            <v-col cols="12">
              <div class="itemFieldName mb-2">Title</div>
            </v-col>

            <v-col cols="4">
              <ValidationProvider v-slot="{ errors }" name="Title (EN)" rules="required">
                <FletrixTextField
                  v-model="localizedTitle.en"
                  label="Title (EN)"
                  :error-messages="errors"
                  @input="onTitleInput"
                />
              </ValidationProvider>
            </v-col>
            <v-col cols="4">
              <ValidationProvider v-slot="{ errors }" name="Title (ZH-HK)" rules="required">
                <FletrixTextField
                  v-model="localizedTitle['zh-hk']"
                  label="Title (ZH-HK)"
                  :error-messages="errors"
                  @input="onTitleInput"
                />
              </ValidationProvider>
            </v-col>
            <!-- <v-col cols="4">
              <ValidationProvider v-slot="{ errors }" name="Title (CN)" rules="required">
                <FletrixTextField
                  v-model="localizedTitle['zh-cn']"
                  label="Title (CN)"
                  :error-messages="errors"
                  @input="onTitleInput"
                />
              </ValidationProvider>
            </v-col> -->

            <v-col cols="12">
              <div class="itemFieldName mb-2">Content</div>
            </v-col>

            <v-col cols="12">
              <div class="itemFieldName mb-2">Content (EN)</div>
              <quillEditor
                ref="quillEn"
                :options="quillOptions"
                v-model="localizedContent.en"
                @change="onContentInput"
              />
            </v-col>
            <v-col cols="12">
              <div class="itemFieldName mb-2">Content (ZH-HK)</div>
              <quillEditor
                ref="quillHk"
                :options="quillOptions"
                v-model="localizedContent['zh-hk']"
                @change="onContentInput"
              />
            </v-col>
            <!-- <v-col cols="12">
              <div class="itemFieldName mb-2">Content (CN)</div>
              <quillEditor
                ref="quillCn"
                :options="quillOptions"
                v-model="localizedContent['zh-cn']"
                @change="onContentInput"
              />
            </v-col> -->
          </v-row>
        </v-card-text>
      </v-card>
    </ValidationObserver>
  </v-container>
</template>

<script>
import { FletrixTextField } from '@/components/Atom';
import { RESTFUL } from '@/data/constants';
import 'quill/dist/quill.core.css';
import 'quill/dist/quill.snow.css';
import 'quill/dist/quill.bubble.css';
import { quillEditor } from 'vue-quill-editor';
import { crudRead, crudCreate, crudUpdate } from '@/api/crud';
import { parseLocalizedString, getOid } from '@/services/utils';
import api from '@/api';
import HeaderLabel from '@/components/customFormLabel/headerLabel.vue';
import { filter } from 'lodash';

export default {
  components: {
    FletrixTextField,
    quillEditor,
    HeaderLabel,
  },
  props: {
    pageType: {
      type: String,
      required: true,
    },
    pageTitle: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      model: {
        metadata: {
          type: this.pageType,
        },
      },
      isSubmitting: false,
      isLoading: true,
      quillOptions: {
        modules: {
          toolbar: {
            container: [
              ['bold', 'italic', 'underline', 'strike'],
              ['blockquote', 'code-block'],
              [{ header: 1 }, { header: 2 }],
              [{ list: 'ordered' }, { list: 'bullet' }],
              [{ script: 'sub' }, { script: 'super' }],
              [{ indent: '-1' }, { indent: '+1' }],
              [{ size: ['small', false, 'large', 'huge'] }],
              [{ header: [1, 2, 3, 4, 5, 6, false] }],
              [{ color: [] }, { background: [] }],
              [{ align: [] }],
              ['clean'],
              ['link'],
            ],
          },
        },
        readOnly: false,
      },
      localizedTitle: {
        en: '',
        'zh-hk': '',
        'zh-cn': '',
      },
      localizedContent: {
        en: '',
        'zh-hk': '',
        'zh-cn': '',
      },
      currentPageId: null,
    };
  },
  computed: {
    crudURL() {
      return RESTFUL.posts.list;
    },
  },
  watch: {
    pageType: {
      immediate: true,
      handler() {
        this.loadPage();
      },
    },
  },
  methods: {
    async loadPage() {
      this.isLoading = true;
      try {
        const response = await api.get(this.crudURL, {
          params: {
            filter: { 'metadata.type': this.pageType },
            limit: 1,
          },
        });

        if (response.data && response.data.docs && response.data.docs.length > 0) {
          const page = response.data.docs[0];
          this.currentPageId = getOid(page);
          this.model = {
            enabled: page.enabled != null ? page.enabled : true,
            metadata: page.metadata || { type: this.pageType },
            title: page.title,
            content: page.content,
          };
          this.localizedTitle = parseLocalizedString(this.model.title);
          this.localizedContent = parseLocalizedString(this.model.content);
        } else {
          this.currentPageId = null;
          this.model = {
            enabled: true,
            metadata: { type: this.pageType },
            title: '',
            content: '',
          };
          this.localizedTitle = {
            en: '',
            'zh-hk': '',
            'zh-cn': '',
          };
          this.localizedContent = {
            en: '',
            'zh-hk': '',
            'zh-cn': '',
          };
        }
      } catch (error) {
        this.$store.dispatch('alert/updateAlertMessage', {
          msg: 'Failed to load page: ' + (error.response?.data?.message || error.message),
          type: 'error',
          color: 'error',
        });
      } finally {
        this.isLoading = false;
      }
    },

    onTitleInput() {
      this.model.title = JSON.stringify(this.localizedTitle);
    },

    onContentInput() {
      this.model.content = JSON.stringify(this.localizedContent);
    },

    async save() {
      if (this.isSubmitting) return;

      this.isSubmitting = true;
      try {
        this.model.title = JSON.stringify(this.localizedTitle);
        this.model.content = JSON.stringify(this.localizedContent);
        this.model.metadata = {
          type: this.pageType,
        };

        if (this.currentPageId) {
          await crudUpdate(api, this.crudURL, this.currentPageId, this.model);
          this.$store.dispatch('alert/updateAlertMessage', {
            msg: 'Page updated successfully',
            type: 'success',
            color: 'success',
          });
        } else {
          const created = await crudCreate(api, this.crudURL, this.model);
          this.currentPageId = getOid(created);
          this.$store.dispatch('alert/updateAlertMessage', {
            msg: 'Page created successfully',
            type: 'success',
            color: 'success',
          });
        }
      } catch (error) {
        this.$store.dispatch('alert/updateAlertMessage', {
          msg: 'Failed to save page: ' + (error.response?.data?.message || error.message),
          type: 'error',
          color: 'error',
        });
      } finally {
        this.isSubmitting = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.itemFieldName {
  font-weight: 500;
  font-size: 14px;
  color: rgba(0, 0, 0, 0.87);
}

::v-deep .ql-editor {
  min-height: 300px;
}
</style>
